// Pure helpers for the V3 start, community and search views (no network, no storage).
import { own, text, participationState, contactGroups, spotsLeft } from "./memberDerive";

const WEEKDAY = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const CLOCK = /^([01]\d|2[0-3]):[0-5]\d$/;
const two = (n) => String(n).padStart(2, "0");

function localDay(v) {
  const m = typeof v === "string" ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(v) : null;
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const day = new Date(Date.UTC(y, mo - 1, d));
  if (day.getUTCFullYear() !== y || day.getUTCMonth() !== mo - 1 || day.getUTCDate() !== d) return null;
  return `${WEEKDAY[day.getUTCDay()]}, ${two(d)}.${two(mo)}.${y}`;
}

function zoned(date, timeZone) {
  const f = new Intl.DateTimeFormat("de-CH", { timeZone, weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });
  const p = Object.fromEntries(f.formatToParts(date).map((x) => [x.type, x.value]));
  return `${p.weekday.replace(".", "")}, ${p.day}.${p.month}.${p.year} · ${p.hour}:${p.minute}`;
}

// "Sa, 31.10.2026 · 14:00–16:00" from the Core's local fields; the UTC date is only a fallback, shown in the activity's time zone.
export function activityWhen(a) {
  const day = localDay(own(a, "date_local"));
  const start = own(a, "start");
  const end = own(a, "end");
  if (day) return CLOCK.test(start) ? `${day} · ${start}${CLOCK.test(end) ? `–${end}` : ""}` : day;
  const iso = own(a, "date");
  const date = typeof iso === "string" ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return "Termin offen";
  // Ortszeit of the activity, never the device's zone; without a valid IANA zone Europe/Zurich (Core default).
  const tz = own(a, "timezone");
  try { return zoned(date, typeof tz === "string" && tz ? tz : "Europe/Zurich"); } catch (_) { return zoned(date, "Europe/Zurich"); }
}

const plainText = (v) => (typeof v === "string" ? v.trim() : "");

export function activityPlace(a) {
  return [plainText(own(a, "location")), plainText(own(a, "meeting_point"))].filter(Boolean).join(" · ") || "Ort folgt";
}

export function spotsText(a) {
  const free = spotsLeft(a);
  if (free === null) return "";
  return free === 0 ? "Ausgebucht" : `${free} von ${Number(a.capacity)} Plätzen frei`;
}

const CONFIRMED = ["confirmed", "registered"];
export const isConfirmedPlan = (a) => CONFIRMED.includes(participationState(a));
export const nextConfirmed = (list) => (Array.isArray(list) ? list.find(isConfirmedPlan) || null : null);

export function greeting(name, hour) {
  const first = typeof name === "string" ? name.trim().split(/\s+/)[0] : "";
  const word = hour < 11 ? "Guten Morgen" : hour < 18 ? "Guten Tag" : "Guten Abend";
  return first ? `${word}, ${first}.` : `${word}.`;
}

// M114 only when the Core stores prefs.display.simplified === true (contract C.2); nothing else switches it on.
export const isSimplified = (profileResponse) => own(own(own(own(profileResponse, "profile"), "prefs"), "display"), "simplified") === true;

export const firstIncoming = (conversations) => contactGroups(Array.isArray(conversations) ? conversations : []).incoming[0] || null;

const fold = (v) => String(v ?? "").toLocaleLowerCase("de").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

// Client-side search over posts the Core already returned for this account; nothing else is searched.
export function searchPosts(posts, query) {
  const q = fold(query);
  if (q.length < 2 || !Array.isArray(posts)) return [];
  return posts.filter((p) => p.status === "visible" && (fold(p.body).includes(q) || fold(p.author && p.author.name).includes(q)));
}

const AREAS = { alle: "all", community: "community", kreis: "group" };
const AREA_PARAM = { all: "alle", community: "community", group: "kreis" };

// Community filter lives in the URL only (M49); unknown values fall back to "all".
export function readCommunityFilter(search) {
  const p = new URLSearchParams(typeof search === "string" ? search : "");
  const filter = Object.prototype.hasOwnProperty.call(AREAS, p.get("bereich") || "") ? AREAS[p.get("bereich")] : "all";
  const groupId = filter === "group" ? (p.get("kreis") || "").slice(0, 100) : "";
  return { filter, groupId, q: (p.get("q") || "").slice(0, 100) };
}

export function communityQuery({ filter, groupId, q }) {
  const p = new URLSearchParams();
  if (filter && filter !== "all") p.set("bereich", AREA_PARAM[filter] || "alle");
  if (filter === "group" && groupId) p.set("kreis", groupId);
  if (q && q.trim()) p.set("q", q.trim());
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const activeFilterCount = ({ filter, groupId, q }) => (filter !== "all" ? 1 : 0) + (filter === "group" && groupId ? 1 : 0) + (q && q.trim() ? 1 : 0);

export const excerpt = (v, max = 80) => {
  const s = text(v).replace(/\s+/g, " ").trim();
  return Array.from(s).length > max ? `${Array.from(s).slice(0, max - 1).join("")}…` : s;
};
